import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { 
  ArrowRight,
  HelpCircle
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MarketingNav, MarketingFooter } from "./LandingPage";

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const faqs = [
  {
    category: "GETTING STARTED",
    questions: [
      {
        q: "What is HeyIntake?",
        a: "HeyIntake is a smart intake form tool that helps service businesses collect, score, and manage leads. Every submission is automatically analyzed by AI, scored from 0–100, and placed into your pipeline for follow-up."
      },
      {
        q: "Who is HeyIntake built for?",
        a: "HeyIntake is designed for consultants, agencies, coaches, freelancers, and service-based teams who rely on inbound inquiries. If you use intake forms or contact forms to collect leads, HeyIntake helps you prioritize and track them."
      },
      {
        q: "How do I create my first form?",
        a: "After signing up, you'll be prompted to create your first form. Choose a template (Project-Based, Appointment-Based, Retainer-Based, or Simple Lead), customize the fields to match your business, and share the link with potential clients."
      }
    ]
  },
  {
    category: "AI SCORING",
    questions: [
      {
        q: "How does AI lead scoring work?",
        a: "When someone submits your form, our AI analyzes their responses and assigns a score from 0–100 based on factors like budget, timeline, urgency, and problem clarity. You also receive an AI-generated summary and recommended follow-up timing."
      },
      {
        q: "What factors affect the lead score?",
        a: "The scoring criteria depend on your form template. For example, Project-Based forms prioritize budget, authority, and timeline. Appointment-Based forms focus on urgency and schedule fit. Each template is optimized for its use case."
      },
      {
        q: "Can I customize the AI scoring?",
        a: "The AI scoring is template-driven. By choosing the right template for your business, you ensure the scoring criteria match what matters to you. Fields marked with 'AI Scoring' badges are critical for accurate scoring."
      }
    ]
  },
  {
    category: "PIPELINE & CRM",
    questions: [
      {
        q: "Do I need a separate CRM?",
        a: "No. HeyIntake includes a built-in pipeline where you can track leads from first submission to closed deal. Update statuses (New, Contacted, Qualified, Won, Lost), add notes, set reminders, and tag leads — all in one place."
      },
      {
        q: "Can I export my leads?",
        a: "Yes. You can export your leads to CSV from the dashboard. This makes it easy to back up your data or import leads into other tools if needed."
      },
      {
        q: "What statuses can I assign to leads?",
        a: "HeyIntake uses a simple 5-stage pipeline: New → Contacted → Qualified → Won → Lost. You can update a lead's status at any time from the lead detail view or using bulk actions."
      }
    ]
  },
  {
    category: "FORMS & TEMPLATES",
    questions: [
      {
        q: "What form templates are available?",
        a: "HeyIntake offers four templates: Project-Based (for agencies, consultants, contractors), Appointment-Based (for wellness, salons, therapists), Retainer-Based (for bookkeepers, VAs, coaches), and Simple Lead (for general contact forms)."
      },
      {
        q: "Can I customize my form fields?",
        a: "Absolutely. After selecting a template, you can add, remove, or edit fields to match your business needs. Just note that some fields are marked as 'AI Scoring' fields — removing them may affect score accuracy."
      },
      {
        q: "Can I have multiple forms?",
        a: "Yes, depending on your plan. Starter plans include 1 form, Professional plans include 5 forms, and Enterprise plans offer unlimited forms. Each form can use a different template."
      }
    ]
  },
  {
    category: "PRICING & BILLING",
    questions: [
      {
        q: "How does the trial work?",
        a: "All new accounts start with a 7-day trial for just $1. You get full access to your chosen plan during the trial. On day 8, your subscription automatically renews at the full plan price unless you cancel."
      },
      {
        q: "What happens after my trial ends?",
        a: "After your 7-day trial, your subscription automatically renews at the full plan price ($19.99/month for Starter or $59.99/month for Professional). You can cancel anytime before the trial ends to avoid being charged."
      },
      {
        q: "Can I change my plan later?",
        a: "Yes. You can upgrade or downgrade your plan at any time from the Settings page. Changes take effect at the start of your next billing cycle."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit cards through our secure payment processor, Stripe."
      },
      {
        q: "What's your refund policy?",
        a: "The $1 trial fee is non-refundable. For monthly subscriptions, you can cancel anytime and retain access until the end of your billing period. We do not offer prorated refunds for partial months."
      }
    ]
  },
  {
    category: "SECURITY & PRIVACY",
    questions: [
      {
        q: "Is my data secure?",
        a: "Yes. HeyIntake uses enterprise-grade security measures to protect your data. All data is encrypted in transit and at rest. Payment information is handled directly by Stripe and never stored on our servers."
      },
      {
        q: "Do you sell my data?",
        a: "No. We never sell your data or your customers' data. Your information is used solely to provide the HeyIntake service."
      },
      {
        q: "Is HeyIntake GDPR compliant?",
        a: "We take privacy seriously and follow best practices for data protection. For specific compliance questions, please contact us at hello@heyintake.com."
      }
    ]
  },
  {
    category: "INTEGRATIONS & AUTOMATION",
    questions: [
      {
        q: "Can HeyIntake integrate with my existing tools and software?",
        a: (
          <div className="space-y-3">
            <p>Yes — HeyIntake integrates with thousands of tools through Zapier, allowing you to automate your lead intake, client onboarding, and workflows without manual work.</p>
            <p className="font-medium">You can connect HeyIntake to popular tools such as:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>CRMs (e.g., Clio, HubSpot, Salesforce)</li>
              <li>Project management tools (e.g., Asana, Trello, ClickUp)</li>
              <li>Scheduling tools (e.g., Calendly, Google Calendar)</li>
              <li>Email platforms (e.g., Gmail, Outlook)</li>
              <li>File storage (e.g., Google Drive, Dropbox)</li>
              <li>Accounting tools (e.g., QuickBooks, Xero)</li>
              <li>Contract & signing tools (e.g., DocuSign, SignWell)</li>
            </ul>
            <p className="font-medium">This means you can automatically:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Create contacts or matters in your CRM</li>
              <li>Send leads to your pipeline</li>
              <li>Trigger follow-ups and tasks</li>
              <li>Store documents in your preferred systems</li>
            </ul>
            <p>No coding required — just connect your tools using a webhook or Zapier.</p>
          </div>
        )
      },
      {
        q: "Does HeyIntake integrate with Clio or legal software?",
        a: "Yes — via Zapier, you can connect HeyIntake with Clio and other legal tools to automatically create matters, contacts, and workflows."
      },
      {
        q: "Can I send leads from HeyIntake to my CRM automatically?",
        a: "Yes — HeyIntake can automatically send leads to CRMs like HubSpot, Salesforce, or others using Zapier or webhooks."
      },
      {
        q: "Does HeyIntake support automation and workflows?",
        a: "Yes — HeyIntake has built-in Auto Flows that trigger on lifecycle events. Auto-reply to new leads, send intake forms when status changes, request documents automatically, and more. You can also create custom automations with the Builder or connect to external tools via Zapier."
      }
    ]
  },
  {
    category: "AI FORM BUILDER",
    questions: [
      {
        q: "What is the AI Form Builder?",
        a: "The AI Form Builder lets you describe your business and goals in plain language, and AI generates a custom lead form with optimized fields, scoring weights, and placeholders. You can fully edit every field — label, type, options, required/optional, and AI scoring weight — before creating the form."
      },
      {
        q: "Can I edit AI-generated fields?",
        a: "Absolutely. The AI-generated form is a starting point, not a final output. Every field is fully editable: change labels, switch types (text, dropdown, radio), add/remove options, toggle required, enable AI scoring, and adjust weights. You can also add or remove fields."
      },
      {
        q: "What happens if I regenerate?",
        a: "If you click Regenerate, HeyIntake will warn you that your current edits will be replaced. You can choose to keep editing or regenerate with a new AI output."
      }
    ]
  },
  {
    category: "AUTOMATIONS",
    questions: [
      {
        q: "What automations come enabled by default?",
        a: "For new accounts, three recommended automations are enabled by default: (1) Respond to new leads instantly (auto-reply email), (2) Notify me when a lead form is submitted, and (3) Send intake form automatically. You can toggle any of these on or off."
      },
      {
        q: "Can I customize the email templates used by automations?",
        a: "Yes. Each automation preset is linked to an email template. Click the edit icon on any automation to view its details and navigate directly to the linked email template for editing."
      },
      {
        q: "What triggers are available for custom automations?",
        a: "Custom automations can trigger on: Form Submitted, Lead Form Submitted, or Status Changed (to any specific status). Actions include sending client emails, sending business notifications, or sending specific forms."
      }
    ]
  },
  {
    category: "CONSENT & COMPLIANCE",
    questions: [
      {
        q: "What is the consent checkbox?",
        a: "The consent checkbox is an optional feature for lead (pre-booking) forms. When enabled, it adds a checkbox at the bottom of your form with customizable text like 'I agree to be contacted regarding my request'. You can make it required or optional."
      },
      {
        q: "Is the consent checkbox the same as the agreement?",
        a: "No. The consent checkbox is a simple opt-in for being contacted, stored in the form schema. The Agreement system is a separate feature for post-booking intake forms where clients must review and accept your terms of service or contract."
      }
    ]
  },
  {
    category: "BRANDING",
    questions: [
      {
        q: "Can I customize how my forms look?",
        a: "Yes. On the Professional plan, you can set a brand color that applies across your forms, emails, and client portal. You can also upload a logo and remove the HeyIntake badge for a fully white-labeled experience."
      },
      {
        q: "Where is branding configured?",
        a: "Branding is configured in Settings > General > Branding. You can also set it up during onboarding or from the dashboard checklist. The brand color picker includes preset swatches and a custom hex input."
      }
    ]
  }
];

export default function FaqPage() {
  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Inter" }}>
      <MarketingNav />

      <section className="pt-36 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-indigo-600 mb-4">FAQ</p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-zinc-950 tracking-tight leading-tight mb-6" style={{ fontFamily: "Inter" }}>Frequently Asked Questions</h1>
            <p className="text-lg text-zinc-500 leading-relaxed max-w-2xl mx-auto">
              Everything you need to know about HeyIntake. Can't find what you're looking for? <a href="mailto:hello@heyintake.com" className="text-indigo-600 hover:underline">Contact us</a>.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 px-6 bg-zinc-50">
        <div className="max-w-3xl mx-auto space-y-12">
          {faqs.map((section, sectionIndex) => (
            <motion.div key={sectionIndex} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: sectionIndex * 0.05 }}>
              <h2 className="text-xs font-bold text-zinc-400 mb-4 tracking-[0.15em] pt-2">{section.category}</h2>
              <div className="bg-white rounded-2xl border border-zinc-200/60 overflow-hidden">
                <Accordion type="single" collapsible className="w-full">
                  {section.questions.map((faq, faqIndex) => (
                    <AccordionItem key={faqIndex} value={`${sectionIndex}-${faqIndex}`} className="border-b border-zinc-100 last:border-0">
                      <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-zinc-50/50 text-left">
                        <span className="font-medium text-zinc-900">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 text-zinc-500">{faq.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-zinc-950 tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Still have questions?</h2>
          <p className="text-lg text-zinc-500 mb-6">We're here to help. Reach out and we'll get back to you as soon as possible.</p>
          <Button className="rounded-full px-8 bg-zinc-950 text-white hover:bg-zinc-800" onClick={() => window.open("mailto:hello@heyintake.com", "_blank")}>Contact Support <ArrowRight className="ml-2 w-5 h-5" /></Button>
        </div>
      </section>

      <section className="py-20 px-6 bg-zinc-950">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: "Inter" }}>Ready to get started?</h2>
          <p className="text-lg text-zinc-400 mb-8">Start your free 7-day trial. No credit card required.</p>
          <Link to="/register"><Button className="h-14 px-10 text-base rounded-full bg-indigo-600 text-white hover:bg-indigo-700">Start Trial <ArrowRight className="ml-2 w-5 h-5" /></Button></Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
